<template>
    <div class="md:flex">
        <form class="flex-1 mb-8 md:pr-4 xl:pr-0">
            <label
                class="heading-font text-sm uppercase font-normal mg-color-secondary-lighter"
                >{{ label }}</label
            >
            <div class="relative">
                <select
                    v-if="options.length"
                    v-model="selected"
                    @change="handleActiveData"
                    class="mg-form-control mg-select w-full rounded-lg"
                >
                    <option
                        :value="option.value"
                        v-for="(option, index) in options"
                        :key="index"
                    >
                        <span v-html="option.text"></span>
                    </option>
                </select>
                <div
                    class="pointer-events-none absolute pin-y pin-r flex items-center px-2 mg-color-accent-2"
                >
                    <svg class="fill-current h-8 w-8">
                        <use
                            href="#icon-angle-down"
                            xlink:href="#icon-angle-down"
                        />
                    </svg>
                </div>
            </div>
        </form>

        <form
            v-if="type == 'commodities' && activeMarket"
            class="md:w-1/2 mb-8 xl:hidden md:pl-4"
        >
            <label
                class="heading-font text-sm uppercase font-normal mg-color-secondary-lighter"
                >Filter by commodity</label
            >
            <div class="relative">
                <select
                    v-model="selectedCommodity"
                    class="mg-form-control mg-select w-full rounded-lg"
                    @change="activateSingleCommodity(selectedCommodity)"
                >
                    <option
                        v-for="(val, key, index) in sortedMarketInfo"
                        :key="index"
                        v-if="isCommodity(key)"
                        :value="key"
                    >
                        <span
                            v-html="
                                getCommodityProperty({
                                    key: key,
                                    property: 'text'
                                })
                            "
                        ></span>
                    </option>
                </select>
                <div
                    class="pointer-events-none absolute pin-y pin-r flex items-center px-2 mg-color-accent-2"
                >
                    <svg class="fill-current h-8 w-8">
                        <use
                            href="#icon-angle-down"
                            xlink:href="#icon-angle-down"
                        />
                    </svg>
                </div>
            </div>
        </form>

        <form
            v-if="type == 'top-export-partners' && topTenMarkets.length"
            class="md:w-1/2 mb-8 xl:hidden md:pl-4"
        >
            <label
                class="heading-font text-sm uppercase font-normal mg-color-secondary-lighter"
                >Filter by country / region</label
            >
            <div class="relative">
                <select
                    v-model="selectedPartner"
                    class="mg-form-control mg-select w-full rounded-lg"
                    @change="activateSinglePartner(selectedPartner)"
                >
                    <option value="World Total">World Total</option>
                    <option
                        v-for="(market, index) in topTenMarkets"
                        :key="index"
                        :value="market.market_name"
                    >
                        <span v-html="market.market_name"></span>
                    </option>
                </select>
                <div
                    class="pointer-events-none absolute pin-y pin-r flex items-center px-2 mg-color-accent-2"
                >
                    <svg class="fill-current h-8 w-8">
                        <use
                            href="#icon-angle-down"
                            xlink:href="#icon-angle-down"
                        />
                    </svg>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from "vuex";
import sortBySubPropInt from "../utilities/sort-by-subprop-int";

export default {
    props: ["type"],
    name: "market-data-select",
    data() {
        return {
            selectedCommodity: "all",
            selectedPartner: "World Total"
        };
    },
    computed: {
        ...mapState([
            "isDesktop",
            "marketDataMode",
            "showQty",
            "commodities",
            "markets",
            "market_data",
            "top_exports_selected",
            "selectedMarket",
            "activeMarket",
            "activeCommodities",
            "activePartners"
        ]),
        ...mapGetters([
            "getCommodityProperty",
            "isCommodity",
            "sortedData",
            "topTenMarkets"
        ]),
        selected: {
            get() {
                if (this.type == "commodities") {
                    return this.selectedMarket;
                } else if (this.type == "top-export-partners") {
                    return this.top_exports_selected;
                }
            },
            set(value) {
                if (this.type == "commodities") {
                    this.setSelectedMarket(value);
                } else if (this.type == "top-export-partners") {
                    this.setTopExportsSelected(value);
                }
            }
        },
        label() {
            if (this.type == "commodities") {
                return "Filter by country / region";
            } else if (this.type == "top-export-partners") {
                return "Filter by commodity";
            } else {
                return "Select";
            }
        },
        options() {
            if (this.type == "commodities") {
                return this.markets;
            } else if (this.type == "top-export-partners") {
                return this.commodities;
            } else {
                return [];
            }
        },
        sortedMarketInfo() {
            if (this.activeMarket) {
                return this.sortedData(this.activeMarket);
            } else {
                return {};
            }
        }
    },
    watch: {
        showQty() {
            if (this.showQty && this.marketDataMode == "USDA-data") {
                this.enableAllOption();
            }
        },
        isDesktop() {
            // `selectedCommodity` is for the mobile/tablet commodity select
            if (!this.isDesktop) {
                if (this.activeCommodities.length == 1) {
                    this.selectedCommodity = this.activeCommodities[0];
                } else {
                    this.selectedCommodity = "";
                }

                if (this.activePartners.length == 1) {
                    this.selectedPartner = this.activePartners[0];
                } else {
                    this.selectedPartner = "";
                }
            }
        },
        activeMarket() {
            if (this.type == "commodities") {
                this.checkMissingCommodities();
            }
        },
        topTenMarkets() {
            if (this.type == "top-export-partners") {
                this.checkMissingPartners();
            }
        }
    },
    methods: {
        ...mapMutations([
            "setMarketData",
            "setTopExportsSelected",
            "setSelectedMarket",
            "addActiveCommodity",
            "deactivateAllCommodities",
            "addActiveCommodity",
            "addActivePartner"
        ]),
        ...mapActions([
            "getMarketDataSettings",
            "getMarketData",
            "getMarketDataFeedGrain",
            "getMarketDataCornEquiv",
            "getExportTotals",
            "getExportTotalsFeedGrain",
            "getExportTotalsCornEquiv",
            "selectMarket",
            "activateAllCommodities",
            "activateSingleCommodity",
            "activateSinglePartner"
        ]),
        checkMissingCommodities() {
            let missingCommoditiesCount = 0;
            this.activeCommodities.forEach((commodity) => {
                if (!this.activeMarket[commodity]) {
                    missingCommoditiesCount++;
                }
            });

            if (missingCommoditiesCount == this.activeCommodities.length) {
                if (this.showQty && this.marketDataMode == "USDA-data") {
                    this.enableAllOption();
                } else {
                    this.selectedCommodity = "all";
                    this.addActiveCommodity("all");
                }
            }
        },
        checkMissingPartners() {
            let partnersFound = 0;
            this.activePartners.forEach((partner) => {
                for (
                    let index = 0;
                    index < this.topTenMarkets.length;
                    index++
                ) {
                    const market = this.topTenMarkets[index];
                    if (partner == market.market_name) {
                        partnersFound++;
                        break;
                    }
                }
            });

            if (
                partnersFound == 0 &&
                this.activePartners.indexOf("World Total") == -1
            ) {
                this.selectedPartner = "World Total";

                if (this.isDesktop) {
                    this.addActivePartner(this.selectedPartner);
                } else {
                    this.activateSinglePartner(this.selectedPartner);
                }
            }
        },
        sortMarketData() {
            const marketData = sortBySubPropInt(this.market_data, {
                property: this.top_exports_selected,
                sub_property: "current_market_year_quantity"
            });
            this.setMarketData(marketData);
        },
        enableAllOption() {
            this.deactivateAllCommodities();
            this.selectedCommodity = "all";
            this.addActiveCommodity("all");
        },
        handleActiveData() {
            if (this.type == "commodities") {
                this.selectMarket();
            } else if (this.type == "top-export-partners") {
                this.sortMarketData();
            }
        },
        afterInitalDataLoad() {
            this.handleActiveData();

            if (this.type == "commodities") {
                if (this.showQty) {
                    this.enableAllOption();
                } else {
                    this.activateAllCommodities();
                }
            }
        },
        init() {
            this.getMarketDataSettings()
                .then(() => {
                    if (
                        this.type == "commodities" &&
                        this.marketDataMode == "GIAF-eq"
                    ) {
                        return Promise.all([
                            this.getExportTotalsFeedGrain(),
                            this.getMarketDataFeedGrain()
                        ]);
                    } else if (
                        this.type == "commodities" &&
                        this.marketDataMode == "corn-eq"
                    ) {
                        return Promise.all([
                            this.getExportTotalsCornEquiv(),
                            this.getMarketDataCornEquiv()
                        ]);
                    } else {
                        return Promise.all([
                            this.getExportTotals(),
                            this.getMarketData()
                        ]);
                    }
                })
                .then(() => {
                    this.afterInitalDataLoad();
                });
        }
    },
    created() {
        this.init();
    }
};
</script>



// WEBPACK FOOTER //
// src/js/vue-components/market-data-select.vue